import { FormData, DisabilityCalculatorResult, ApiData, TFPRateEntry, VacationLookupEntry, Assumptions, NECRates, MBCBPRates, TaxBrackets } from "../Models/disability-calculator";
import { getMonthlyIncomeTableData } from "../Services/monthlyIncomeTableData";
import { getMonthlyRetirementTableData } from "../Services/monthlyRetirementTableData";
import { getAnnualIncomeTableData } from "../Services/annualIncomeTableData";
import { getAnnualRetirementTableData } from "../Services/annualRetirementTableData";
import { getTFPRate, parseUTCDate } from "../utils/utils";
import { HttpClient } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, retry } from "rxjs/operators";
import {
  generateUnifiedTableCacheKey,
  clearUnifiedTableCaches,
} from "../utils/unifiedTable";
import {ASSUMPTIONS} from "../constants/constants";
export class DisabilityCalculator {
  private http: HttpClient;
  private apiURL: string;
  private lastFormDataHash: string | null;
  public apiData: ApiData | null;
  public vacationRatesFromAPI: VacationLookupEntry[];
  public tfpRatesFromAPI: TFPRateEntry[];
  public assumptionsFromAPI : any;

  constructor(http: HttpClient) {
    this.http = http;
    this.apiURL =
      typeof window !== "undefined" && window.location
        ? window.location.href
        : "";
    this.lastFormDataHash = null;
    this.apiData = null;
    this.vacationRatesFromAPI = [];
    this.tfpRatesFromAPI = [];
    this.assumptionsFromAPI = [];
  }
  setApiData(apiResponse: ApiData): void {
    this.apiData = apiResponse;
    this.vacationRatesFromAPI = apiResponse.Rate || [];
    this.tfpRatesFromAPI = apiResponse.TfpRates || [];
    this.assumptionsFromAPI = this.mergeAssumptions(apiResponse.Assumptions);
  }

  calculate(formData: FormData): DisabilityCalculatorResult {
    try {
      formData.vacationRates = this.vacationRatesFromAPI;
      formData.tfpRates = this.tfpRatesFromAPI;
      formData.assumptions = this.assumptionsFromAPI || ASSUMPTIONS;
      // Check if formData has changed, only clear cache if it has
      const currentFormDataHash = generateUnifiedTableCacheKey(formData);
      if (this.lastFormDataHash !== currentFormDataHash) {
        clearUnifiedTableCaches();
        this.lastFormDataHash = currentFormDataHash;
      }
      const monthlyIncomeTableData = getMonthlyIncomeTableData(formData);
      const monthlyRetirementTableData = [];
      // const monthlyRetirementTableData = getMonthlyRetirementTableData(
      //   formData,
      //   monthlyIncomeTableData
      // );
      const annualIncomeTableData = getAnnualIncomeTableData(formData);
      const annualRetirementTableData = getAnnualRetirementTableData(formData);

      return {
        monthlyIncomeTableData,
        monthlyRetirementTableData,
        annualIncomeTableData,
        annualRetirementTableData,
      };
    } catch (error) {
      throw new Error("Calculation failed: " + (error as Error).message);
    }
  }

  getDisabilityCalcDetails(): Observable<ApiData> {
    const backToTopstring = this.apiURL.indexOf("#");
    if (backToTopstring !== -1) {
      this.apiURL = this.apiURL.substring(0, backToTopstring);
    }
    const queryString = this.apiURL.indexOf("?");
    if (queryString !== -1) {
      // this.setQstringSectId(this.apiURL.substring(this.apiURL.indexOf('=') + 1, this.apiURL.length))
      this.apiURL = this.apiURL.substring(0, queryString);
    }
    return this.http.get<ApiData>(this.apiURL + "/GetInputJson/").pipe(
      retry(1),
      catchError((err) => {
        return throwError(() => err);
      })
    );
  }

  /**
   * Calculate longevity (Years of Service) based on hire date and disability date
   * @param hireDate - Hire date string
   * @param disabilityDate - Disability date string
   * @returns Longevity in years
   */
  calculateLongevity(hireDate: string, disabilityDate: string): number {
    if (!hireDate || !disabilityDate) {
      return 0;
    }

    const hireDateObj = parseUTCDate(hireDate);
    const disabilityDateObj = parseUTCDate(disabilityDate);

    if (isNaN(hireDateObj.getTime()) || isNaN(disabilityDateObj.getTime())) {
      return 0;
    }

    // Calculate longevity (Years of Service)
    const longevity =
      disabilityDateObj.getUTCFullYear() - hireDateObj.getUTCFullYear() + 1;

    return longevity;
  }

  calculateWeeksOfVacation(yearsOfService: number): number {
    if(yearsOfService !== 0)
      return this.getDaysValueFromVacationLookup(yearsOfService) / 7; // Years Of Service / days of week(7)
    return 0;
  }

  getTFPRate(rateCode: string, year: number): number {
    return getTFPRate(rateCode, year, this.tfpRatesFromAPI);
  }

  getDaysValueFromVacationLookup(longevity: number): number {
   const vacationLookup = this.vacationRatesFromAPI;
    
    // Find closest YOS entry in vacation lookup
    let daysValue = 0;
    if (vacationLookup && vacationLookup.length > 0) {
      const lookupEntry = vacationLookup.find(entry => entry.YOS === longevity);
      
      if (lookupEntry && lookupEntry.PerMonth) {
        daysValue = lookupEntry.Days;
      } else {
        // If exact match not found, find closest YOS
        const sortedEntries = vacationLookup.sort((a, b) => Math.abs(a.YOS - longevity) - Math.abs(b.YOS - longevity));
        
        if (sortedEntries.length > 0 && sortedEntries[0].Days) {
          daysValue = sortedEntries[0].Days;
        }
      }
    }
    return daysValue;
  }

  mergeAssumptions(
  apiResponse: Partial<Assumptions> | null | undefined
): Assumptions {

  // fallback to empty object if apiResponse is null/undefined
  const safeApi = apiResponse || {};

  const getValidValue = (value: string | string[] | undefined | null | NECRates | MBCBPRates , defaultValue: any) => {
    // Return the default if value is null, undefined, or a "falsy" string ("0" or "")
    return (value === null || value === undefined || value === "" || value === "0") ? defaultValue : value;
  };

  return {
    MED: getValidValue(safeApi.MED, ASSUMPTIONS.MED),
    SSPect: getValidValue(safeApi.SSPect, ASSUMPTIONS.SSPect),
    NECRates: getValidValue(safeApi.NECRates, ASSUMPTIONS.NECRates),
    MBCBPRates: getValidValue(safeApi.MBCBPRates, ASSUMPTIONS.MBCBPRates),
    AssumptionByYear: safeApi.AssumptionByYear
  };
}
}
